import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, CheckCircle2, Loader2, Paperclip, Send, X } from 'lucide-react';
import { useCreateBugReport } from '../../hooks/useBugReports';
import { useUploadQueue } from '../../hooks/useUploadQueue';
import { uploadBugDraftFile, type BugAttachment } from '../../lib/bugReportsApi';
import { UploadChipList } from '../ui/UploadProgress';
import { useI18n } from '../../contexts/I18nContext';

const OPEN_EVENT = 'firmio:open-bug-report';

/** Opens the bug-report panel; called from the sidebar's "Raportează un bug". */
export function openBugReport() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/**
 * The "report a bug" panel. There is no floating button: the sidebar entry
 * opens it through `openBugReport()`, so the corner holds only the team chat.
 *
 * Mounted once in Layout, so it follows you across every authenticated page and
 * never shows up on the login screens. The panel is anchored to the button's
 * corner rather than being a centred modal: you are reporting what is on the
 * screen behind it, so covering the screen would be the one thing not to do.
 *
 * Screenshots upload the moment they are dropped, before the report exists —
 * hence the report-less `POST /api/bug-reports/upload-url`.
 */
export function BugReportWidget() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pending, setPending] = useState<BugAttachment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filed, setFiled] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const { uploads, isUploading, run } = useUploadQueue();
  const create = useCreateBugReport();
  const titleRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (open) titleRef.current?.focus();
  }, [open]);

  // Escape closes the panel from anywhere inside it, like the other overlays.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // The chat pages put their composer at the bottom of the content column, so
  // the panel sits higher there instead of covering the send button.
  const isChat = pathname.startsWith('/chat') || pathname.startsWith('/team-chat');
  const bottom = isChat ? 'bottom-20' : 'bottom-4';

  function reset() {
    setTitle('');
    setDescription('');
    setPending([]);
    setError(null);
    setFiled(null);
  }

  async function handleFiles(files: FileList | File[]) {
    const list = Array.from(files);
    if (list.length === 0) return;
    setError(null);
    try {
      for (const file of list) {
        // Chip by chip: a finished screenshot is attached and visible while the
        // rest of the batch is still going.
        const uploaded = await run(file, (onProgress) => uploadBugDraftFile(file, onProgress));
        setPending((prev) => [...prev, uploaded]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('bugReport.error.upload'));
    }
  }

  async function submit() {
    if (!canSubmit) return;
    setError(null);
    try {
      const report = await create.mutateAsync({
        title: title.trim(),
        description: description.trim(),
        attachments: pending,
        // Captured here, not asked of the reporter: this is the difference
        // between "it's broken" and something reproducible.
        context: {
          pageUrl: `${window.location.pathname}${window.location.search}`,
          userAgent: navigator.userAgent,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          appVersion: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? null,
        },
      });
      setFiled(report.identifier);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('bugReport.error.generic'));
    }
  }

  const canSubmit =
    !create.isPending && !isUploading && title.trim().length > 0 && description.trim().length > 0;

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t('bugReport.widget.title')}
      // Phones: a sheet on the bottom edge, full width (a card floating 16px
      // above it looked misplaced, with the page showing underneath).
      className={`bug-report-panel fixed right-4 ${bottom} z-[70] flex max-h-[min(32rem,calc(100dvh-2rem))] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-2xl max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-h-[85dvh] max-sm:w-full max-sm:rounded-none max-sm:rounded-t-2xl max-sm:border-x-0 max-sm:border-b-0 max-sm:pb-[env(safe-area-inset-bottom)] sm:w-96`}
    >
      <header className="flex items-center justify-between border-b border-border bg-brand-600 px-4 py-3 text-white">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Bug size={16} /> {t('bugReport.widget.title')}
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={t('bugReport.widget.close')}
          className="rounded p-1 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
        >
          <X size={16} />
        </button>
      </header>

      {filed ? (
        <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
          <CheckCircle2 size={32} className="text-green-600" />
          <p className="text-sm font-semibold text-gray-900">{t('bugReport.success.title')}</p>
          <p className="text-sm text-gray-500">
            {t('bugReport.success.body', { identifier: filed })}
          </p>
          <button
            type="button"
            onClick={reset}
            className="mt-1 text-sm font-medium text-brand-700 hover:underline"
          >
            {t('bugReport.success.again')}
          </button>
        </div>
      ) : (
        <div
          className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3"
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (e.dataTransfer.files.length) void handleFiles(e.dataTransfer.files);
          }}
        >
          <p className="text-xs text-gray-500">{t('bugReport.widget.intro')}</p>

          <label className="block">
            <span className="ui-field-label">{t('bugReport.field.title')}</span>
            <input
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('bugReport.field.titlePlaceholder')}
              maxLength={200}
              className="ui-input mt-1 w-full"
            />
          </label>

          <label className="block">
            <span className="ui-field-label">{t('bugReport.field.description')}</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              // Ctrl/⌘+Enter sends, the same shortcut as every composer here.
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void submit();
                }
              }}
              onPaste={(e) => {
                const images = Array.from(e.clipboardData.items).filter((item) =>
                  item.type.startsWith('image/'),
                );
                if (images.length) {
                  e.preventDefault();
                  void handleFiles(
                    images.map((item) => item.getAsFile()).filter((f): f is File => !!f),
                  );
                }
              }}
              rows={4}
              placeholder={t('bugReport.field.descriptionPlaceholder')}
              className="ui-textarea mt-1 w-full"
            />
          </label>

          <div
            className={`rounded-md border border-dashed px-3 py-2 transition-colors ${
              dragging ? 'border-brand-500 bg-brand-50' : 'border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] leading-snug text-gray-500">
                {t('bugReport.attachments.hint')}
              </span>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                title={t('bugReport.attachments.pick')}
                aria-label={t('bugReport.attachments.pick')}
                className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
              >
                {isUploading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Paperclip size={14} />
                )}
              </button>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept="image/*,application/pdf,text/plain"
                hidden
                onChange={(e) => {
                  if (e.target.files?.length) void handleFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </div>

            {(pending.length > 0 || uploads.length > 0) && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <UploadChipList uploads={uploads} />
                {pending.map((attachment, index) => (
                  <span
                    key={`${attachment.name}-${index}`}
                    className="inline-flex max-w-full items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-500"
                  >
                    <Paperclip size={10} />
                    <span className="truncate">{attachment.name}</span>
                    <button
                      type="button"
                      onClick={() => setPending((prev) => prev.filter((_, i) => i !== index))}
                      aria-label={t('bugReport.attachments.remove')}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {error && <p className="text-[11px] leading-relaxed text-red-600">{error}</p>}
        </div>
      )}

      {!filed && (
        <footer className="flex justify-end border-t border-border px-4 py-2">
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40"
          >
            <Send size={13} />
            {create.isPending ? t('bugReport.submitting') : t('bugReport.submit')}
          </button>
        </footer>
      )}
    </div>
  );
}
